import { Select as ChakraSelect, createListCollection, Portal } from '@chakra-ui/react';
import { forwardRef, useMemo, useRef } from 'react';
import type { StyleProps } from '../../../types';
import { createSyntheticChangeEvent, popFieldWrapperProps } from '../../../utils';
import { FieldWrapper } from '../FieldWrapper';

import { SelectDefaultProps, type SelectProps } from './Select.types';

const Select = forwardRef<HTMLButtonElement, SelectProps>(
  ({ options, placeholder, onChange, 'aria-label': ariaLabel, ...props }, ref) => {
    const { poppedProps, otherProps } = popFieldWrapperProps(props);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleValueChange = (details: { value: Array<string> }) => {
      if (onChange) {
        const syntheticEvent = createSyntheticChangeEvent(
          inputRef.current,
          JSON.stringify(details.value)
        );
        onChange(syntheticEvent);
      }
    };

    const selectCollection = useMemo(
      () =>
        createListCollection({
          items: options || [],
        }),
      [options]
    );

    const selectOptions = useMemo(
      () =>
        options?.map((item) => (
          <ChakraSelect.Item
            item={item}
            key={item.value}
            _hover={{
              cursor: 'pointer',
            }}
          >
            {item.label}
            <ChakraSelect.ItemIndicator />
          </ChakraSelect.Item>
        )) || null,
      [options]
    );

    return (
      <FieldWrapper {...poppedProps}>
        <ChakraSelect.Root
          collection={selectCollection}
          onValueChange={handleValueChange}
          {...otherProps}
        >
          <input type="hidden" ref={inputRef} />
          <ChakraSelect.Control>
            <ChakraSelect.Trigger ref={ref} aria-label={ariaLabel} {...SelectStyles}>
              <ChakraSelect.ValueText placeholder={placeholder} />
            </ChakraSelect.Trigger>
            <ChakraSelect.IndicatorGroup>
              <ChakraSelect.Indicator />
            </ChakraSelect.IndicatorGroup>
          </ChakraSelect.Control>
          <Portal>
            <ChakraSelect.Positioner>
              {/* Chakra/Ark UI's default Content width matches the trigger's width
                  (var(--reference-width)) — long option labels get clipped and force
                  a horizontal scrollbar instead of the menu growing to fit them. */}
              <ChakraSelect.Content width="max-content" minWidth="100%">
                {selectOptions}
              </ChakraSelect.Content>
            </ChakraSelect.Positioner>
          </Portal>
        </ChakraSelect.Root>
      </FieldWrapper>
    );
  }
);

const SelectStyles: StyleProps = {
  borderColor: 'border',
  bg: 'bg',
  _hover: {
    cursor: 'pointer',
  },
};

Select.displayName = 'Select';
Select.defaultProps = SelectDefaultProps;

export { Select };
